import { apiClient } from "./client";
import type { TokenPair } from "./auth";

export type SsoKind = "GOOGLE" | "MICROSOFT" | "OIDC";

export type PublicProvider = {
  id: string;
  kind: SsoKind;
  display_name: string;
};

export type SsoProvider = {
  id: string;
  tenant_id: string;
  kind: SsoKind;
  display_name: string;
  issuer: string;
  client_id: string;
  /** The secret itself never leaves the API — only whether one is stored. */
  has_client_secret: boolean;
  allowed_email_domains: string[];
  enabled: boolean;
  /** To paste into the IdP console. Fixed for the whole deployment. */
  redirect_uri: string;
  linked_identities: number;
};

export type MyIdentity = {
  id: string;
  provider_id: string;
  provider_display_name: string;
  kind: SsoKind;
  email_at_link: string | null;
  last_login_at: string | null;
};

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
/** Which SSO buttons to offer for this address. Empty for unknown accounts. */
export async function providersForEmail(email: string): Promise<PublicProvider[]> {
  const { data } = await apiClient.get<PublicProvider[]>("/api/v1/auth/sso/providers", {
    params: { email },
  });
  return data;
}

export async function startSsoLogin(
  providerId: string,
  nextPath?: string,
): Promise<{ authorization_url: string; state: string }> {
  const { data } = await apiClient.post<{ authorization_url: string; state: string }>(
    `/api/v1/auth/sso/${providerId}/start`,
    null,
    { params: nextPath ? { next_path: nextPath } : undefined },
  );
  return data;
}

export async function finishSsoLogin(state: string, code: string): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>("/api/v1/auth/sso/callback", {
    state,
    code,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Self-service linking — the migration path off passwords
// ---------------------------------------------------------------------------
export async function myIdentities(): Promise<MyIdentity[]> {
  const { data } = await apiClient.get<MyIdentity[]>("/api/v1/me/sso");
  return data;
}

export async function linkableProviders(): Promise<PublicProvider[]> {
  const { data } = await apiClient.get<PublicProvider[]>("/api/v1/me/sso/available");
  return data;
}

export async function startSsoLink(
  providerId: string,
): Promise<{ authorization_url: string; state: string }> {
  const { data } = await apiClient.post<{ authorization_url: string; state: string }>(
    `/api/v1/me/sso/link/${providerId}/start`,
  );
  return data;
}

export async function finishSsoLink(
  state: string,
  code: string,
): Promise<{ identity: MyIdentity; linked_email: string }> {
  const { data } = await apiClient.post<{ identity: MyIdentity; linked_email: string }>(
    "/api/v1/me/sso/link/callback",
    { state, code },
  );
  return data;
}

export async function unlinkIdentity(identityId: string): Promise<void> {
  await apiClient.delete(`/api/v1/me/sso/${identityId}`);
}

// ---------------------------------------------------------------------------
// Administration
// ---------------------------------------------------------------------------
export async function listSsoProviders(): Promise<SsoProvider[]> {
  const { data } = await apiClient.get<SsoProvider[]>("/api/v1/admin/sso-providers");
  return data;
}

export type SsoProviderInput = {
  kind: SsoKind;
  display_name: string;
  client_id: string;
  client_secret?: string;
  issuer?: string;
  allowed_email_domains?: string[];
  enabled?: boolean;
};

export async function createSsoProvider(input: SsoProviderInput): Promise<SsoProvider> {
  const { data } = await apiClient.post<SsoProvider>("/api/v1/admin/sso-providers", input);
  return data;
}

export async function updateSsoProvider(
  id: string,
  patch: Partial<SsoProviderInput>,
): Promise<SsoProvider> {
  const { data } = await apiClient.patch<SsoProvider>(`/api/v1/admin/sso-providers/${id}`, patch);
  return data;
}

export async function deleteSsoProvider(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/sso-providers/${id}`);
}
